import React from 'react';
import {
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  TextField,
  TextArea,
  Text,
} from '@chatsdet/ui';
import { ApiError } from '../../../lib/api-error';
import type { PageSkill } from '../types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  skill: PageSkill | null;
  isSaving: boolean;
  onSave: (edit: {
    title: string;
    summary: string;
    body: string;
    baseVersion: number;
  }) => Promise<unknown>;
};

/**
 * Edit an agent-authored page skill.
 *
 * The pattern is shown but not editable: skill identity is
 * (application, matchType, pathPattern), so changing it would fork a second
 * skill rather than rename this one.
 *
 * Saving carries the `baseVersion` the form was loaded from. If the browser
 * agent wrote a new version while this was open, the server rejects the save
 * instead of discarding that write, and we surface a re-read prompt rather than
 * silently retrying — the agent's observation may be newer and better than the
 * human edit in progress.
 */
export function EditPageSkillDialog({ isOpen, onClose, skill, isSaving, onSave }: Props) {
  const [title, setTitle] = React.useState('');
  const [summary, setSummary] = React.useState('');
  const [body, setBody] = React.useState('');
  const [conflict, setConflict] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setTitle(skill?.title ?? '');
    setSummary(skill?.summary ?? '');
    setBody(skill?.body ?? '');
    setConflict(null);
    setError(null);
  }, [skill]);

  if (!skill) return null;

  const dirty = title !== skill.title || summary !== skill.summary || body !== skill.body;
  const valid = title.trim() && summary.trim() && body.trim();

  const handleSave = async () => {
    if (!valid || !dirty) return;
    setConflict(null);
    setError(null);
    try {
      await onSave({
        title: title.trim(),
        summary: summary.trim(),
        body: body.trim(),
        baseVersion: skill.currentVersion,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PAGE_SKILL_VERSION_CONFLICT') {
        const actual = (err.details as { actualVersion?: number } | undefined)?.actualVersion;
        setConflict(typeof actual === 'number' ? actual : skill.currentVersion + 1);
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to save page skill');
    }
  };

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Button className="hidden">Trigger</Button>
      <Dialog
        title="Edit page skill"
        description={`${skill.pathPattern} · v${skill.currentVersion}`}
      >
        <DialogContent>
          <div className="space-y-4">
            {conflict !== null && (
              <div className="rounded-lg border border-warning/50 bg-warning/10 p-3 space-y-2">
                <Text size="sm">
                  The browser agent updated this skill while you were editing (it is now v
                  {conflict}). Your changes were not saved, so the agent&apos;s newer observation
                  is intact. Close and reopen to edit the current version.
                </Text>
              </div>
            )}
            {error && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                <Text size="sm" className="text-destructive">
                  {error}
                </Text>
              </div>
            )}

            <div>
              <Text size="xs" className="text-muted-foreground">
                Trigger: {skill.pathPattern} ({skill.matchType}) — not editable; re-targeting a
                skill means archiving this one and letting the agent author the new pattern.
              </Text>
            </div>

            <TextField label="Title" value={title} onChange={setTitle} />
            <TextArea label="Summary" value={summary} onChange={setSummary} rows={2} />
            <TextArea
              label="Body (Markdown)"
              value={body}
              onChange={setBody}
              rows={18}
              placeholder="What an agent needs to know before acting on this page…"
            />
            <Text size="xs" className="text-muted-foreground">
              {body.length} characters. Saving appends a version — the current text stays
              readable in History.
            </Text>
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleSave}
            isDisabled={!valid || !dirty || isSaving || conflict !== null}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
